import type { ComponentPropsWithRef, FC, ReactNode } from "react"

import { cx } from "@uui/utils/cx"

/*
 * Empty is provisional: the provisional line at the region cut, a condensed
 * headline, one action. No illustration, no featured-icon disc — the line says
 * "nothing landed here yet", and an empty state should teach what would.
 *
 * Keeps the kit's compound shape (Root/Header/FeaturedIcon/Content/Title/
 * Description/Footer) so call sites do not change; the icon renders quietly.
 */
const Root = ({
  size = "lg",
  className,
  ...props
}: ComponentPropsWithRef<"div"> & { size?: "sm" | "md" | "lg" }) => (
  <div
    {...props}
    data-size={size}
    className={cx(
      "provisional mx-auto flex w-full flex-col items-center text-center",
      size === "sm" && "max-w-sm gap-3 px-5 py-8",
      size === "md" && "max-w-md gap-4 px-6 py-12",
      size === "lg" && "max-w-lg gap-5 px-8 py-16",
      className,
    )}
  />
)

const Header = (props: ComponentPropsWithRef<"div">) => (
  <div
    {...props}
    className={cx("flex items-center justify-center", props.className)}
  />
)

const FeaturedIcon = ({
  icon: Icon,
  className,
}: {
  icon?: FC<{ className?: string }>
  color?: string
  theme?: string
  size?: string
  className?: string
}) =>
  Icon ? (
    <Icon
      aria-hidden="true"
      className={cx("text-fg-quaternary size-6 stroke-[1.5]", className)}
    />
  ) : null

const Illustration = (props: { type?: string; className?: string }) =>
  props ? null : null

const Content = (props: ComponentPropsWithRef<"div">) => (
  <div {...props} className={cx("flex flex-col gap-1.5", props.className)} />
)

const Title = (props: ComponentPropsWithRef<"h2">) => (
  <h2 {...props} className={cx("text-primary text-lg", props.className)} />
)

const Description = (props: ComponentPropsWithRef<"p">) => (
  <p
    {...props}
    className={cx("text-tertiary text-sm text-pretty", props.className)}
  />
)

const Footer = (props: ComponentPropsWithRef<"footer">) => (
  <footer
    {...props}
    className={cx("flex flex-wrap justify-center gap-3", props.className)}
  />
)

export const EmptyState = Object.assign(Root, {
  Header,
  FeaturedIcon,
  Illustration,
  Content,
  Title,
  Description,
  Footer,
}) as typeof Root & {
  Header: typeof Header
  FeaturedIcon: typeof FeaturedIcon
  Illustration: typeof Illustration
  Content: typeof Content
  Title: typeof Title
  Description: typeof Description
  Footer: typeof Footer
}

export type { ReactNode as EmptyStateChildren }
